// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import { colors, layout } from '@/theme';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import * as Enums from '@/utils/enums';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import Helper from '@/utils/helper';
import InvoiceCard from '@/components/customer-zone/invoice/invoice-card';
import PageContext from '@/utils/page-context';
import SCListCardSection from '@/components/sc-controls/layout/sc-list-card-section';
import useMobileView from '@/hooks/useMobileView';
import NoSSR from '@/utils/no-ssr';
import CompanyService from '@/services/company-service';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import { createFileRoute } from '@tanstack/react-router'

const pageSize = 10;

const unpaidStatus = [Enums.getEnumStringValue(Enums.InvoiceStatus, Enums.InvoiceStatus.Overdue),
Enums.getEnumStringValue(Enums.InvoiceStatus, Enums.InvoiceStatus.Unpaid)];
const paidStatus = [Enums.getEnumStringValue(Enums.InvoiceStatus, Enums.InvoiceStatus.Paid)];

function InvoiceList() {

  const toast = useContext<any>(ToastContext);
  const pageContext = useContext<any>(PageContext);
  const [mobileView] = useMobileView();
  const router = useRouter();
  const { t: tParam, c: cParam } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch
  const t = typeof tParam === 'string' ? tParam : undefined;
  const c = typeof cParam === 'string' ? cParam : undefined;

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t } as any),
    { enabled: !!t }
  );
  const tenant: any = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0 ? tenantData.Results[0] : null;

  const { data: unpaidInvoicesData } = useQuery(
    ['czUnpaidInvoices', t, c],
    () => Fetch.post({
      url: '/CustomerZone/GetInvoices',
      params: {
        PageSize: pageSize,
        SortExpression: "StartDate",
        SortDirection: "descending",
        InvoiceStatusIDList: unpaidStatus,
        PopulatedList: false
      },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant }
  );

  const { data: paidInvoicesData } = useQuery(
    ['czPaidInvoices', t, c],
    () => Fetch.post({
      url: '/CustomerZone/GetInvoices',
      params: {
        PageSize: pageSize,
        SortExpression: "StartDate",
        SortDirection: "descending",
        InvoiceStatusIDList: paidStatus,
        PopulatedList: false
      },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant }
  );

  const { data: currencyData } = useQuery(
    ['czCurrencySymbol', t, c],
    () => CompanyService.getCustomerZoneCurrencySymbol(t, c, tenant.API),
    { enabled: !!tenant }
  );

  const [unpaidInvoiceResults, setUnpaidInvoiceResults] = useState<any[] | null>(null);
  const [unpaidInvoiceTotalResults, setUnpaidInvoiceTotalResults] = useState(0);
  const [unpaidCurrentPage, setUnpaidCurrentPage] = useState(0);

  const [paidInvoiceResults, setPaidInvoiceResults] = useState<any[] | null>(null);
  const [paidInvoiceTotalResults, setPaidInvoiceTotalResults] = useState(0);
  const [paidCurrentPage, setPaidCurrentPage] = useState(0);

  const [currencySymbol, setCurrencySymbol] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (tenantData && !tenant) { router.replace('/'); }
  }, [tenantData]);

  useEffect(() => {
    if (unpaidInvoicesData?.headerTenantName) {
      pageContext.updateTenantName(unpaidInvoicesData.headerTenantName);
    }
  }, [unpaidInvoicesData]);

  useEffect(() => {
    if (unpaidInvoicesData && !unpaidInvoiceResults) {
      setUnpaidInvoiceResults(unpaidInvoicesData.Results);
      setUnpaidInvoiceTotalResults(unpaidInvoicesData.TotalResults);
    }
  }, [unpaidInvoicesData]);

  useEffect(() => {
    if (paidInvoicesData && !paidInvoiceResults) {
      setPaidInvoiceResults(paidInvoicesData.Results);
      setPaidInvoiceTotalResults(paidInvoicesData.TotalResults);
    }
  }, [paidInvoicesData]);

  useEffect(() => {
    if (currencyData !== undefined && currencySymbol === '') {
      setCurrencySymbol(currencyData);
    }
  }, [currencyData]);

  useEffect(() => {
    if (unpaidCurrentPage > 0)
      searchInvoices(unpaidStatus, unpaidCurrentPage, unpaidInvoiceResults, setUnpaidInvoiceResults, setUnpaidInvoiceTotalResults);
  }, [unpaidCurrentPage]);

  useEffect(() => {
    if (paidCurrentPage > 0)
      searchInvoices(paidStatus, paidCurrentPage, paidInvoiceResults, setPaidInvoiceResults, setPaidInvoiceTotalResults);
  }, [paidCurrentPage]);

  async function searchInvoices(status: any, currentPage: any, invoiceResults: any, setInvoiceResults: any, setTotalResults: any) {
    setSearching(true);

    const searchRes = await Fetch.post({
      url: '/CustomerZone/GetInvoices',
      params: {
        pageIndex: currentPage,
        PageSize: pageSize,
        SortExpression: "StartDate",
        SortDirection: "descending",
        InvoiceStatusIDList: status,
        PopulatedList: false
      },
      toastCtx: toast,
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    });

    if (currentPage < 1) {
      setInvoiceResults(searchRes.Results);
      setTotalResults(searchRes.TotalResults);
    } else {
      let temp = [...invoiceResults];
      temp.push(...searchRes.Results);
      setInvoiceResults(temp);
      setTotalResults(searchRes.TotalResults);
    }
    setSearching(false);
  }

  function invoiceClick(invoice: any) {
    Helper.nextRouter(router.push, `/customerzone/viewinvoice?t=${t}&c=${c}&i=${invoice.ID}`);
  }

  if (!unpaidInvoiceResults || !paidInvoiceResults) return null;

  return (
    <NoSSR>
      <div className={mobileView ? "" : "centered"}
           style={{
             paddingInlineEnd: 'var(--mantine-spacing-sm)',
             minHeight: 'calc(100dvh - 20px)'
           }}
      >

        <LeadMessage customerID={c} tenantID={t} page={"customerzone/invoicelist"} />

        <h2 className="page-title">Your Invoices</h2>

        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>

        {unpaidInvoiceResults && unpaidInvoiceResults.length > 0 ? <>
          <SCListCardSection text="Unpaid" colour={Enums.getEnumStringValue(Enums.InvoiceStatusColor, Enums.InvoiceStatus.Unpaid) as any} />
          {unpaidInvoiceResults.map((invoice: any, key: any) => {
            return <InvoiceCard key={key} invoice={invoice} onClick={(i: any) => invoiceClick(i)} currencySymbol={currencySymbol} />
          })}
          {unpaidInvoiceResults.length < unpaidInvoiceTotalResults ?
            <div className="load-more" onClick={() => setUnpaidCurrentPage(unpaidCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {paidInvoiceResults && paidInvoiceResults.length > 0 ? <>
          <SCListCardSection text="Paid" colour={Enums.getEnumStringValue(Enums.InvoiceStatusColor, Enums.InvoiceStatus.Paid) as any} />
          {paidInvoiceResults.map((invoice: any, key: any) => {
            return <InvoiceCard key={key} invoice={invoice} onClick={(i: any) => invoiceClick(i)} currencySymbol={currencySymbol} />
          })}
          {paidInvoiceResults.length < paidInvoiceTotalResults ?
            <div className="load-more" onClick={() => setPaidCurrentPage(paidCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {(!unpaidInvoiceResults || unpaidInvoiceResults.length === 0) && (!paidInvoiceResults || paidInvoiceResults.length === 0) ? <>
          <h4>There are no invoices</h4>
        </> : ""}

        <style jsx>{`
        .load-more {
            margin: 0.5rem;
            cursor: pointer;
            font-weight: bold;
            text-align: right;
            ${mobileView ? "" : `max-width: ${layout.listCardWidth};`}
        }

        .load-more:hover {
            text-decoration: underline;
        }

        h2.page-title {
          margin: 0;
          font-size: 1.5rem;
        }

        .container {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          padding: 1.5rem 3rem;
          overflow-x: hidden;
        }
        .column {
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 9rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .title {
          align-items: center;
          display: flex;
          color: ${colors.darkPrimary};
          width: 100%;
        }
        .title h1 {
          font-size: 24px;
          margin: 6px 0 0 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .end {
          align-items: flex-end;
        }
        .padded {
          padding-bottom: 1rem;
        }
        .row.top-gap {
          margin-top: 2.5rem;
        }
        .heading {
          color: ${colors.blueGrey};
          font-size: 14px;
          margin-bottom: 0.5rem;
        }
        .heading-blue {
          color: ${colors.bluePrimary};
        }
        .no-invoices {
          align-items: center;
          box-sizing: border-box;
          display: none;
          flex-direction: column;
          flex-grow: 1;
          height: 100%;
          justify-content: center;
          position: relative;
          text-align: center;
        }
        .no-invoices-visible {
          display: flex;
        }
        .no-invoices h3 {
          color: ${colors.darkPrimary};
          font-size: 18px;
          line-height: 22px;
          margin: 2rem 0 0;
        }
        .no-invoices p {
          color: ${colors.blueGrey};
          margin: 0.5rem 0 0;
        }
        .no-invoices a {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
          margin-top: 1rem;
          text-decoration: none;
        }
        .no-invoices a img{
          margin-right: 0.5rem;
        }
        .wave {
          position: absolute;
          bottom: -1.5rem;
          left: -3rem;
          pointer-events: none;
          right: 0;
          width: calc(100% + 6rem);
        }
        .search-container :global(.search) {
          width: 528px;
        }
        .table-container {
          display: block;
          height: 100%;
          overflow: none;
          position: relative;
        }
        .table-container :global(.table) {
          margin-top: 0;
        }
        .table-container-visible {
          display: block;
        }
        .loading-overlay {
          align-items: center;
          background-color: rgba(245, 248, 251, 0.6);
          bottom: 0;
          border-radius: 8px;
          display: none;
          justify-content: center;
          left: -1rem;
          position: absolute;
          right: -1rem;
          top: 0.5rem;
        }
        .loading-overlay-visible {
          display: flex;
        }
        .loading-overlay :global(.loader) {
          border-color: rgba(28, 37, 44, 0.2);
          border-left-color: ${colors.darkPrimary};
          border-width: 0.25rem;
          display: flex;
          height: 1.5rem;
          width: 1.5rem;
        }
        a {
          text-decoration: none;
        }
        .select-actions {
          display: flex;
          margin-left: -1rem;
        }
        .select-actions :global(.white-action) {
          font-size: 0.875rem;
          margin: 0 0 0 1rem; 
          width: auto;
        }
        .select-actions :global(.icon) {
          left: 1rem !important;
        }
        .download {
          margin-right: 1rem;
          width: 3rem;
        }

        .centered {
          position: relative;
          left: 50%;
          transform: translateX(-50%);
          width: ${layout.listCardWidth};
        }
      `}</style>
      </div>
    </NoSSR>
  );
}

export default InvoiceList;

export const Route = createFileRoute('/customerzone/invoicelist')({ component: InvoiceList })
